/**
 * The signed-in partner's own API credential — the client_id and client_secret their systems use to
 * call this API directly instead of going through the browser.
 *
 * ONE RULE GOVERNS THIS WHOLE FILE: the client_secret exists in this process for as long as the
 * person is looking at it, and nowhere else. It is never written to localStorage, never put in a
 * URL, never logged, and never re-fetched — because it cannot be. `generateClientSecret` is the only
 * call that returns one, the server keeps only a hash of it (see
 * server/src/oauth/clientCredentials/), and `getApiCredential` exists precisely so the screen can
 * describe the live secret without asking for it.
 *
 * AND NOT A FRAGMENT OF IT EITHER. An earlier cut of this screen showed the secret's first eight
 * characters so an owner could tell which one was installed. The rule is now absolute — no part of a
 * client_secret is readable after it is created — so the dates do that job instead. There is nothing
 * in `ApiCredential` that could sign in, or help anyone guess something that could.
 */
import axios from 'axios';
import type { AxiosError } from 'axios';
import { apiBaseUrl } from './apiBase.ts';
import { DOCS_ROUTE_PREFIX } from '../../../shared/src/api/routePaths.ts';
import { getAccessToken } from './authClient.ts';

/** Everything the screen may know about a credential — server's ApiCredentialSummary. */
export interface ApiCredential {
  /** Public, re-readable, and stable once minted. Rotating a secret does not change it. */
  clientId: string;
  /** False after a revoke: the id still exists, but nothing can sign in with it. */
  hasSecret: boolean;
  /**
   * When the live secret was generated. Note what is NOT here and never will be: the secret, or any
   * fragment of it. This date and lastUsedAt are how an owner tells whether the value sitting in their
   * configuration is the current one.
   */
  secretSetAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface GeneratedClientSecret {
  /** In clear, and for the only time it will ever be sent to anyone. */
  clientSecret: string;
  credential: ApiCredential;
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const endpoint = () => `${apiBaseUrl()}/rest/api-credentials`;

/**
 * Where the Swagger UI lives. On the API's own origin, so its Try-it-out calls are same-origin.
 *
 * The PATH comes from shared/src/api/routePaths.ts rather than being written out here, and that is
 * the whole fix for Oct 2026: this button used to open `<api base>/docs`, which on the deployment
 * where the app and the API share an origin is not the API at all — the proxy in front of them
 * forwards only /rest, /oauth and /webhooks, and answered /docs with the app's own index.html. The
 * partner who clicked it got the portal dashboard back, with nothing to say the documentation had
 * been swallowed on the way.
 */
export function apiDocsUrl(): string {
  return `${apiBaseUrl()}${DOCS_ROUTE_PREFIX}`;
}

/**
 * Turn a failure into something the owner can act on.
 *
 * The one case worth naming is MFA_REQUIRED, and it is not an error so much as a missing step: both
 * generating and revoking a credential are behind two-step sign-in, so a session that only ever
 * presented a password gets a 403 here. Saying "something went wrong" would send them looking for a
 * fault that isn't there.
 */
export function explainCredentialError(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<ErrorBody> | undefined;
  const code = axiosErr?.response?.data?.error?.code;
  if (code === 'MFA_REQUIRED' || code === 'MFA_SETUP_REQUIRED') {
    return 'API credentials need two-step sign in. Set up your authenticator under Account, then sign out and back in with a code.';
  }
  const serverMsg = axiosErr?.response?.data?.error?.message;
  if (serverMsg) return serverMsg;
  if ((err as Error)?.message === 'Not signed in') return 'Your session has expired — please sign in again.';
  if (!axiosErr?.response) return "We couldn't reach the server. Check your connection and try again.";
  return fallback;
}

/** The credential as it stands, or null if none has ever been generated. Never any secret. */
export async function getApiCredential(): Promise<ApiCredential | null> {
  const headers = await authHeaders();
  const res = await axios.get(endpoint(), { headers, timeout: 30_000 });
  return (res.data?.credential as ApiCredential | null) ?? null;
}

/**
 * Mint a new client_secret, invalidating the previous one.
 *
 * The caller MUST show the returned secret to the user immediately and tell them it will not be
 * shown again — there is no second chance and no recovery path, by design.
 */
export async function generateClientSecret(): Promise<GeneratedClientSecret> {
  const headers = await authHeaders();
  const res = await axios.post(endpoint(), {}, { headers, timeout: 30_000 });
  return res.data as GeneratedClientSecret;
}

/** Withdraw the live secret. The client_id survives, so a new secret drops into the same config. */
export async function revokeClientSecret(): Promise<{ revoked: boolean }> {
  const headers = await authHeaders();
  const res = await axios.delete(endpoint(), { headers, timeout: 30_000 });
  return { revoked: Boolean(res.data?.revoked) };
}
